import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Download, FileText, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface PDFDownloadButtonProps {
  productName?: string;
  pdfFileName?: string;
  className?: string;
  variant?: "primary" | "card";
}

export const PDFDownloadButton = ({ 
  productName = "Aureum VII Trading", 
  pdfFileName = "AVT_Smart_Toilet_Catalog.pdf",
  className = "",
  variant = "primary"
}: PDFDownloadButtonProps) => {
  
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = `/files/${pdfFileName}`;
    link.download = `${productName.replace(/\s+/g, '_')}-Catalog.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (variant === "card") {
    return (
      <Card className={`shadow-soft hover:shadow-luxury transition-all duration-300 border-primary/20 bg-gradient-to-br from-background via-secondary/30 to-muted/30 ${className}`}>
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-gradient-to-br from-accent to-primary rounded-2xl flex items-center justify-center shadow-lg">
              <FileText className="w-8 h-8 text-white" />
            </div>
            
            <div>
              <h3 className="font-semibold text-foreground mb-2 text-lg">
                📄 Full Product Catalog
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Complete luxury smart toilet collection & specifications
              </p>
            </div>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    onClick={handleDownload}
                    className="w-full bg-gradient-to-r from-accent via-primary to-primary hover:from-accent/90 hover:via-primary/90 hover:to-primary/90 text-primary-foreground font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 min-h-[48px] touch-manipulation"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    📄 Download Full Catalog (PDF)
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm">Includes dimensions, installation instructions, and product features</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Info className="w-3 h-3" />
              <span>PDF • Instant Download • 2.5MB</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              onClick={handleDownload}
              size="lg"
              className="w-full sm:w-auto bg-gradient-to-r from-accent via-primary to-primary hover:from-accent/90 hover:via-primary/90 hover:to-primary/90 text-primary-foreground font-semibold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 min-h-[52px] touch-manipulation text-base"
            >
              <Download className="w-5 h-5 mr-3" />
              📄 Download Full Catalog (PDF)
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-sm">Includes dimensions, installation instructions, and product features</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      <p className="text-xs text-muted-foreground text-center sm:text-left flex items-center justify-center sm:justify-start gap-1">
        <Info className="w-3 h-3" />
        Includes technical specifications, installation guide & warranty information
      </p>
    </div>
  );
};

export default PDFDownloadButton;